import React from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import styles from "./_css/headerBlock.module.css";
import clsx from "clsx";
import { geoZonesStore } from "geoZones/_stores/geoZonesStore";
import { PROPERTY_PURPOSE, PROPERTY_TYPE, PROPERTY_TYPE_BY_PURPOSE } from "properties/_models/PropertyMdl";
import SearchBar from "components/sections/searchBlock/SearchBar";
import { homeSearchStore } from "home/_stores/homeSearchStore";
import cuisineDesign from "../../assets/images/hero/cuisine-design-moderne-quebec.webp";
import facadeLogement from "../../assets/images/hero/facade-logement-neuf-moderne-quebec.webp";
import facadeEtages from "../../assets/images/hero/facade-moderne-etages-quebec.webp";
import facadesLogements from "../../assets/images/hero/facades-logements-neufs-quebec.webp";
import grandCondo from "../../assets/images/hero/grand-condo-neuf-quebec.webp";
import grandSalon from "../../assets/images/hero/grand-salon-moderne-quebec.webp";
import piscineCondo from "../../assets/images/hero/piscine-condo-moderne-quebec.webp";
import salonNuit from "../../assets/images/hero/salon-de-nuit-moderne-quebec.webp";
import salonNeuf from "../../assets/images/hero/salon-neuf-chaleureux-quebec.webp";
import salonEpure from "../../assets/images/hero/salon-style-epure-moderne-quebec.webp";

const DAY_MS = 86400000;

// Les 10 images du hero (assets locaux optimisés en webp).
const HERO_IMAGES = [
    cuisineDesign,
    facadeLogement,
    facadeEtages,
    facadesLogements,
    grandCondo,
    grandSalon,
    piscineCondo,
    salonNuit,
    salonNeuf,
    salonEpure,
];

export const HeaderBlock = observer(function HeaderBlock() {
    const { t } = useTranslation();
    const purpose = homeSearchStore.purpose;
    const selectedType = homeSearchStore.propertyType;

    // Une seule image par jour, qui change à minuit heure de l'Est (Canada).
    // On calcule la date locale du Québec via Intl (déterministe SSR + client)
    // pour obtenir un index de jour stable et sans mismatch d'hydratation.
    const dayImage = React.useMemo(() => {
        const parts = new Intl.DateTimeFormat("en-CA", {
            timeZone: "America/Montreal",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).formatToParts(new Date());
        const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
        const easternDate = `${get("year")}-${get("month")}-${get("day")}`;
        const dayIndex = Math.floor(Date.parse(`${easternDate}T00:00:00Z`) / DAY_MS);
        return HERO_IMAGES[dayIndex % HERO_IMAGES.length];
    }, []);

    const getChipLabel = (type: PROPERTY_TYPE) =>
        type === PROPERTY_TYPE.condo && purpose === PROPERTY_PURPOSE.BUY
            ? t("property.types.condoWithoutApartment")
            : t(`property.types.${type}`);

    return (
        <div className={clsx("position_relative mb_80", styles.component)}>
            <div className={styles.background} style={{ backgroundImage: `url(${dayImage})` }} />
            <div className={styles.overlay} />
            <div className={styles.container}>
                <div className={styles.inner}>
                    <h1
                        className={styles.title}
                        dangerouslySetInnerHTML={{
                            __html: t("homePage.headerBlock.title"),
                        }}
                    />
                    <div className={clsx("mt_30 mb_20", styles.buttons)} onClick={() => geoZonesStore.open(false)}>
                        <div
                            className={styles.buttonsSlider}
                            style={{
                                transform: purpose === PROPERTY_PURPOSE.RENT ? "translateX(100%)" : "translateX(0)",
                            }}
                        />
                        {Object.keys(PROPERTY_PURPOSE).map((propertyPurpose) => (
                            <div
                                key={propertyPurpose}
                                className={clsx(styles.button, {
                                    [styles.isFiltered]: propertyPurpose === purpose,
                                })}
                                onClick={() => homeSearchStore.setPurpose(propertyPurpose as PROPERTY_PURPOSE)}
                            >
                                {t(`propertyPage.${propertyPurpose}`)}
                            </div>
                        ))}
                    </div>
                    <div id="recherche" className={styles.searchContainer}>
                        <SearchBar purpose={purpose} />
                    </div>
                    <div className={clsx("mt_20", styles.chips)}>
                        <div
                            className={clsx(styles.chip, { [styles.chipActive]: !selectedType })}
                            onClick={() => homeSearchStore.setPropertyType(undefined)}
                        >
                            {t("homePage.headerBlock.chips.any")}
                        </div>
                        {PROPERTY_TYPE_BY_PURPOSE[purpose].map((type) => (
                            <div
                                key={type}
                                className={clsx(styles.chip, {
                                    [styles.chipActive]: selectedType === type,
                                })}
                                onClick={() => homeSearchStore.setPropertyType(type)}
                            >
                                {getChipLabel(type)}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
});
